import MeetingsService from '@/services/MeetingsService'

function normalizeMeeting(m) {
  if (!m || typeof m !== 'object') return null
  return {
    id: Number(m.id) || 0,
    code: String(m.code ?? ''),
    title: String(m.title ?? ''),
    description: String(m.description ?? ''),
    scheduled_at: m.scheduled_at || null,
    ends_at: m.ends_at || null,
    timezone: String(m.timezone ?? 'America/Phoenix'),
    type: String(m.type ?? 'scheduled'),
    status: String(m.status ?? 'pending'),
    options: m.options && typeof m.options === 'object' ? m.options : {},
    created_by: Number(m.created_by) || 0,
    livekit_room: String(m.livekit_room ?? ''),
    participants: Array.isArray(m.participants) ? m.participants.map(p => ({
      id: Number(p.id) || 0,
      user_id: p.user_id ? Number(p.user_id) : null,
      email: p.email || null,
      name: String(p.name ?? ''),
      role: String(p.role ?? 'participant'),
      status: String(p.status ?? 'invited'),
      joined_at: p.joined_at || null,
      left_at: p.left_at || null,
    })) : [],
    created_at: m.created_at || null,
    updated_at: m.updated_at || null,
  }
}

export default {
  namespaced: true,

  state: () => ({
    // CRUD
    items: [],
    current: null,
    loading: false,
    saving: false,
    error: null,

    // Active call state
    activeCall: {
      meetingId: null,
      token: null,
      wsUrl: null,
      room: null,
      options: null,
      status: 'idle', // idle | connecting | active | ended
    },

    // Incoming video invitation
    incomingInvite: null,
  }),

  getters: {
    list: (s) => s.items,
    current: (s) => s.current,
    byId: (s) => (id) => s.items.find(x => x.id === Number(id)) || null,
    activeCall: (s) => s.activeCall,
    incomingInvite: (s) => s.incomingInvite,
    isInCall: (s) => s.activeCall.status === 'connecting' || s.activeCall.status === 'active',
  },

  mutations: {
    SET_LOADING(state, v) { state.loading = !!v },
    SET_SAVING(state, v) { state.saving = !!v },
    SET_ERROR(state, v) { state.error = v || null },
    SET_LIST(state, rows) { state.items = Array.isArray(rows) ? rows : [] },

    UPSERT_ONE(state, row) {
      const m = normalizeMeeting(row)
      if (!m?.id) return
      const i = state.items.findIndex(x => x.id === m.id)
      if (i >= 0) state.items.splice(i, 1, { ...state.items[i], ...m })
      else state.items.unshift(m)
      if (state.current?.id === m.id) state.current = { ...state.current, ...m }
    },

    SET_CURRENT(state, row) {
      state.current = row ? normalizeMeeting(row) : null
    },

    REMOVE_ONE(state, id) {
      const numId = Number(id)
      state.items = state.items.filter(x => x.id !== numId)
      if (state.current?.id === numId) state.current = null
    },

    SET_ACTIVE_CALL(state, { meetingId, token, wsUrl, room, options, status }) {
      state.activeCall = {
        meetingId: meetingId ?? null,
        token: token ?? null,
        wsUrl: wsUrl ?? null,
        room: room ?? null,
        options: options ?? null,
        status: status ?? 'idle',
      }
    },

    SET_ACTIVE_CALL_STATUS(state, status) {
      state.activeCall.status = status
    },

    SET_INCOMING_INVITE(state, invite) {
      state.incomingInvite = invite
    },

    CLEAR_INCOMING_INVITE(state) {
      state.incomingInvite = null
    },
  },

  actions: {
    // ─── CRUD ────────────────────────────────────────────────

    async fetchList({ commit }, params = {}) {
      commit('SET_LOADING', true)
      commit('SET_ERROR', null)
      try {
        const res = await MeetingsService.list(params)
        const rows = MeetingsService.unwrapList(res).map(normalizeMeeting).filter(Boolean)
        commit('SET_LIST', rows)
        return rows
      } catch (e) { commit('SET_ERROR', e); throw e }
      finally { commit('SET_LOADING', false) }
    },

    async fetchOne({ commit }, id) {
      if (!id) throw new Error('Missing meeting id')
      commit('SET_LOADING', true)
      commit('SET_ERROR', null)
      try {
        const res = await MeetingsService.get(id)
        const row = normalizeMeeting(MeetingsService.unwrapOne(res))
        commit('UPSERT_ONE', row)
        commit('SET_CURRENT', row)
        return row
      } catch (e) { commit('SET_ERROR', e); throw e }
      finally { commit('SET_LOADING', false) }
    },

    async create({ commit }, payload = {}) {
      commit('SET_SAVING', true)
      commit('SET_ERROR', null)
      try {
        const res = await MeetingsService.create(payload)
        const row = normalizeMeeting(MeetingsService.unwrapOne(res))
        commit('UPSERT_ONE', row)
        commit('SET_CURRENT', row)
        return row
      } catch (e) { commit('SET_ERROR', e); throw e }
      finally { commit('SET_SAVING', false) }
    },

    async update({ commit }, { id, payload = {} }) {
      if (!id) throw new Error('Missing meeting id')
      commit('SET_SAVING', true)
      commit('SET_ERROR', null)
      try {
        const res = await MeetingsService.update(id, payload)
        const row = normalizeMeeting(MeetingsService.unwrapOne(res))
        commit('UPSERT_ONE', row)
        commit('SET_CURRENT', row)
        return row
      } catch (e) { commit('SET_ERROR', e); throw e }
      finally { commit('SET_SAVING', false) }
    },

    async destroy({ commit }, id) {
      if (!id) throw new Error('Missing meeting id')
      commit('SET_SAVING', true)
      commit('SET_ERROR', null)
      try {
        await MeetingsService.destroy(id)
        commit('REMOVE_ONE', id)
        return true
      } catch (e) { commit('SET_ERROR', e); throw e }
      finally { commit('SET_SAVING', false) }
    },

    // ─── Video Call Actions ──────────────────────────────────

    async joinMeeting({ commit }, { meetingId }) {
      commit('SET_ACTIVE_CALL', { meetingId, status: 'connecting' })
      try {
        const res = await MeetingsService.getToken(meetingId)
        const data = MeetingsService.unwrapOne(res)
        commit('SET_ACTIVE_CALL', {
          meetingId,
          token: data.token,
          wsUrl: data.ws_url,
          room: data.room,
          options: data.options || null,
          status: 'connecting',
        })
        if (data.meeting) {
          commit('UPSERT_ONE', data.meeting)
        }
        return data
      } catch (e) {
        commit('SET_ACTIVE_CALL', { status: 'idle' })
        throw e
      }
    },

    async directCall({ commit }, { targetUserId }) {
      commit('SET_SAVING', true)
      try {
        const res = await MeetingsService.directCall(targetUserId)
        const data = MeetingsService.unwrapOne(res)
        commit('SET_ACTIVE_CALL', {
          meetingId: data.meeting?.id ?? null,
          token: data.token,
          wsUrl: data.ws_url,
          room: data.room,
          status: 'connecting',
        })
        if (data.meeting) {
          commit('UPSERT_ONE', data.meeting)
        }
        return data
      } catch (e) { throw e }
      finally { commit('SET_SAVING', false) }
    },

    setIncomingInvite({ commit }, payload) {
      commit('SET_INCOMING_INVITE', payload)
    },

    dismissInvite({ commit }) {
      commit('CLEAR_INCOMING_INVITE')
    },

    async acceptInvite({ commit, state, dispatch }) {
      const invite = state.incomingInvite
      if (!invite?.meeting_id) return
      commit('CLEAR_INCOMING_INVITE')
      return dispatch('joinMeeting', { meetingId: invite.meeting_id })
    },

    endCall({ commit }) {
      commit('SET_ACTIVE_CALL', { status: 'idle' })
    },

    setCallActive({ commit }) {
      commit('SET_ACTIVE_CALL_STATUS', 'active')
    },
  }
}
